import { LoadingIndicator, SearchBox, Checkbox } from '@monash/portal-react';
import { useContext, useState, useMemo, useRef, useCallback } from 'react';
import { SlideOutContext } from '../../common/slide-out/SlideOutWrapper';
import cs from '../shared/links-shared.module.scss';
import LinkForm from '../link-form/LinkForm';
import { useApi, getAllLinks } from '@monash/portal-frontend-common';
import NewFormButton from '../../common/new-form-button/NewFormButton';
import BulkActionsModal from '../shared/bulk-actions-modal/BulkActionsModal';
import BulkActionsButtons from '../shared/bulk-actions-buttons/BulkActionsButtons';
import * as Sentry from '@sentry/browser';
import { useEnrichedLinks } from '../shared/hooks/use-enriched-links';
import { bySearch } from '../shared/utils/by-search';
import { byLatestSave } from '../shared/utils/by-latest-save';
import { useLinksState } from '../shared/hooks/use-links-state';
import { useCheckedLinks } from '../shared/hooks/use-checked-links';
import ExpandableLinkCard from '../shared/expandable-link-card/ExpandableLinkCard';
import { useStore } from '@longsien/react-store';
import { categoriesStore } from '../shared/stores';
import { getAllCategories } from '@monash/portal-frontend-common/dist/api/api';
import FiltersButton from '../shared/filters/filters-button/FiltersButton';
import FiltersModal from '../shared/filters/filters-modal/FiltersModal';
import StatusFiltersTags from '../shared/filters/filters-tags/status-filters-tags/StatusFiltersTags';
import {
  getCategoryFilterOptions,
  getCohortFilterOptions,
  getStatusFilterOptions,
} from '../shared/utils/get-filter-options';
import CategoryFiltersTags from '../shared/filters/filters-tags/category-filters-tags/CategoryFiltersTags';
import CohortFiltersTags from '../shared/filters/filters-tags/cohort-filters-tags/CohortFiltersTags';
import { byStatus } from '../shared/utils/by-status';
import { byCategory } from '../shared/utils/by-category';
import { byCohort } from '../shared/utils/by-cohort';

const AllLinks = () => {
  const { setSlide, open, setOpen, slide } = useContext(SlideOutContext);

  const [bulkActionsModalShown, setBulkActionsModalShown] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const bulkActionTriggerRef = useRef();

  const [categories, setCategories] = useStore(categoriesStore);

  useApi({
    requestFn: getAllCategories,
    onSuccess: (response) => {
      setCategories(response);
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Categories data has failed to load.');
    },
  });

  const [filtersModalShown, setFiltersModalShown] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCohorts, setSelectedCohorts] = useState([]);

  const statusFilterOptions = getStatusFilterOptions();
  const categoryFilterOptions = getCategoryFilterOptions(categories);
  const cohortFilterOptions = getCohortFilterOptions();

  const { links, setLinks, addLink, updateLink, deleteLink } = useLinksState();

  const { isLoading: isLoadingLinks } = useApi({
    requestFn: getAllLinks,
    onSuccess: (response) => {
      setLinks(response);
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Links data has failed to load.');
    },
  });

  const enrichedLinks = useEnrichedLinks({ links, categories });

  const [searchTarget, setSearchTarget] = useState('');

  const processedLinks = useMemo(() => {
    return enrichedLinks
      .filter(bySearch(searchTarget))
      .filter(byStatus(selectedStatuses))
      .filter(byCategory(selectedCategories))
      .filter(byCohort(selectedCohorts))
      .sort(byLatestSave);
  }, [
    enrichedLinks,
    searchTarget,
    selectedStatuses,
    selectedCategories,
    selectedCohorts,
  ]);

  const {
    checkedLinks,
    handleCheck,
    handleCheckAll,
    updateChecked,
    isChecked,
    isAllChecked,
  } = useCheckedLinks({ links: processedLinks });

  const openSlideForm = useCallback(
    (link) => {
      setSlide({
        content: (
          <LinkForm
            existing={link}
            addLink={addLink}
            updateLink={updateLink}
            deleteLink={deleteLink}
            cancel={() => setOpen(false)}
            key={link ? link.id : Math.random()} // to update form values when clicking on different link cards
          />
        ),
        data: link,
      });
      setOpen(true);
    },
    [setSlide, setOpen]
  );

  return (
    <div className={cs.container}>
      <div className={cs.allLinks}>
        <div className={cs.header}>
          <h1>Links</h1>
          <NewFormButton
            isMinimised={open}
            onClick={() => {
              openSlideForm(null);
            }}
            aria-label="Add link"
          >
            Add link
          </NewFormButton>
        </div>
        <div>
          <SearchBox searchString={searchTarget} onInput={setSearchTarget} />
        </div>

        {isLoadingLinks ? (
          <div className={cs.loading}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            <div className={cs.filtersWrapper}>
              <FiltersButton onClick={() => setFiltersModalShown(true)} />
              <StatusFiltersTags
                options={statusFilterOptions}
                selectedValues={selectedStatuses}
                onRemove={(v) =>
                  setSelectedStatuses((prevStatuses) =>
                    prevStatuses.filter((status) => status !== v)
                  )
                }
              />
              <CategoryFiltersTags
                options={categoryFilterOptions}
                selectedValues={selectedCategories}
                onRemove={(v) =>
                  setSelectedCategories((prevCats) =>
                    prevCats.filter((cat) => cat !== v)
                  )
                }
              />
              <CohortFiltersTags
                options={cohortFilterOptions}
                selectedValues={selectedCohorts}
                onRemove={(v) =>
                  setSelectedCohorts((prevCohorts) =>
                    prevCohorts.filter((cohort) => cohort !== v)
                  )
                }
              />
              <FiltersModal
                open={filtersModalShown}
                onClose={() => setFiltersModalShown(false)}
                statusFilterOptions={statusFilterOptions}
                selectedStatuses={selectedStatuses}
                onChangeStatuses={setSelectedStatuses}
                categoryFilterOptions={categoryFilterOptions}
                selectedCategories={selectedCategories}
                onChangeCategories={setSelectedCategories}
                cohortFilterOptions={cohortFilterOptions}
                selectedCohorts={selectedCohorts}
                onChangeCohorts={setSelectedCohorts}
              />
            </div>
            <div className={cs.secondaryHeader}>
              <div className={cs.secondaryHeaderActions}>
                <Checkbox on={isAllChecked} onChange={handleCheckAll} />
                <BulkActionsButtons
                  selectedLinks={checkedLinks}
                  setBulkAction={setBulkAction}
                  setBulkActionsModalShown={setBulkActionsModalShown}
                  bulkActionTriggerRef={bulkActionTriggerRef}
                />
              </div>

              <p>{`${processedLinks.length} ${
                processedLinks.length === 1 ? 'link' : 'links'
              }`}</p>
            </div>
            <div className={cs.linksList}>
              {processedLinks.map((link) => {
                const id = link.id;

                return (
                  <ExpandableLinkCard
                    key={id}
                    link={link}
                    selected={open && slide?.data?.id === id}
                    openSlideForm={openSlideForm}
                    checked={isChecked(id)}
                    handleCheck={handleCheck}
                  />
                );
              })}
            </div>
            <BulkActionsModal
              selectedLinks={checkedLinks}
              addLink={addLink}
              updateLink={updateLink}
              deleteLink={deleteLink}
              updateChecked={updateChecked}
              open={bulkActionsModalShown}
              onClose={() => {
                setBulkActionsModalShown(false);
                bulkActionTriggerRef.current?.focus();
              }}
              action={bulkAction}
              triggerRef={bulkActionTriggerRef}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AllLinks;
